import React, { useEffect, useState } from 'react';
import { Dropdown, DropdownItem, DropdownList, SideModal } from '@cokitchen/cokitchen-components';
import { ReactComponent as GiftIcon } from 'assets/icons/gift-icon.svg';
import { FilterAltIcon, SlantArrowDownIcon } from 'cokitchen-icons';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';
import {
  calendarDateFormat,
  formatCurrency,
  handleApiError,
  notificationDateRange
} from 'utils/functions';
import { ENUM_NOTIFICATION_FILTERS } from 'utils/enum';
import InfiniteScroll from 'react-infinite-scroll-component';
import './styles.scss';
import { CompanyTransactionType } from 'models/transactions.model';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const dropdownOptions = [
  { name: ENUM_NOTIFICATION_FILTERS.TODAY },
  { name: ENUM_NOTIFICATION_FILTERS.THIS_WEEK },
  { name: ENUM_NOTIFICATION_FILTERS.LAST_WEEK },
  { name: ENUM_NOTIFICATION_FILTERS.THIS_MONTH }
];

const WalletTransactionsModal = ({ isOpen, onClose }: Props) => {
  const { activeCompany } = useDashboardContext();
  const [showDropdownOptions, setShowDropdownOptions] = useState(false);
  const [filter, setFilter] = useState<string>(dropdownOptions[3].name);
  const defaultRange = ENUM_NOTIFICATION_FILTERS.THIS_MONTH;
  const [startDate, setStartDate] = useState(notificationDateRange(defaultRange).startDate);
  const [endDate, setEndDate] = useState(notificationDateRange(defaultRange).endDate);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<CompanyTransactionType[]>([]);
  const [page, setPage] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const companyId = activeCompany?.id;
  const prefillStartEndDate = () => {
    const { startDate: start, endDate: end } = notificationDateRange(
      filter as ENUM_NOTIFICATION_FILTERS
    );
    setStartDate(start);
    setEndDate(end);
  };

  useEffect(() => {
    prefillStartEndDate();
  }, [filter]);

  const fetchTransactions = async (isNew?: boolean) => {
    if (isNew) setLoading(true);
    try {
      const res = await apiService.get(`transactions/company/${companyId}`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          page: isNew ? 1 : page
        }
      });
      const { data } = res.data;
      setTotalTransactions(data.pagination.total);
      setTransactions(isNew ? data.data : [...transactions, ...data.data]);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTransactions(true);
  }, [companyId, startDate, endDate]);

  useEffect(() => {
    if (page > 1) {
      fetchTransactions();
    }
  }, [page]);

  const fetchMoreData = () => {
    setPage((p) => p + 1);
  };
  return (
    <SideModal
      closeBtnVariant='side'
      bodyClassName='w-[98vw] sm:w-[450px]'
      showTopNavbar
      title={
        <div className='flex justify-between items-center mb-6'>
          <p className='text-[20px] text-[#2E3031] font-medium'>Wallet Transactions</p>
          <Dropdown
            visible={showDropdownOptions}
            onClickOutside={() => setShowDropdownOptions(false)}
            content={
              <div
                className='flex gap-2 cursor-pointer items-center text-[#5E6164] text-sm'
                onClick={() => setShowDropdownOptions(true)}>
                {filter ? filter : 'Filter'}
                <FilterAltIcon />
              </div>
            }>
            <DropdownList>
              {dropdownOptions.map((option, index) => (
                <DropdownItem
                  key={index}
                  onClick={() => {
                    setFilter(option.name);
                    setShowDropdownOptions(false);
                    setPage(1);
                  }}>
                  {option.name}
                </DropdownItem>
              ))}
            </DropdownList>
          </Dropdown>
        </div>
      }
      isOpen={isOpen}
      onRequestClose={() => {
        onClose();
        setPage(1);
      }}>
      <p className='text-[#6F7376] text-xs mb-4'>{filter}</p>
      <div className='w-full h-[30vh] wallet_transactions'>
        {loading ? (
          <div className='w-full h-10 flex justify-center items-center mt-8'>
            <p>Loading...</p>
          </div>
        ) : transactions?.length === 0 ? (
          <div className='w-full h-10 flex justify-center items-center mt-8'>
            <p>No transactions available</p>
          </div>
        ) : (
          <InfiniteScroll
            loader={
              <p className='text-sm flex justify-center mt-8'>Loading more transactions...</p>
            }
            dataLength={transactions?.length}
            next={() => {
              fetchMoreData();
            }}
            hasMore={totalTransactions > transactions.length}
            height={520}
            className='infinite-scroll-container'
            endMessage={
              <p style={{ textAlign: 'center' }} className='text-sm'>
                <b>Yay! You have seen it all</b>
              </p>
            }>
            {transactions.map((item, index) => (
              <div className='flex justify-between mb-[18px]' key={index}>
                <div className='flex gap-4'>
                  <div className='w-[30px] h-[30px] bg-[#F4F1F1] rounded-full flex justify-center items-center'>
                    {item.tx_type === 'reversal' ? (
                      <SlantArrowDownIcon className='w-5 h-4 slant-arrow' />
                    ) : (
                      <GiftIcon className='w-4 h-4' />
                    )}
                  </div>
                  <div>
                    <p className='text-[#5E6164] text-sm'>
                      {item.description}{' '}
                      <span className='text-[#2E3031] font-medium'>
                        {formatCurrency(item.amount)}
                      </span>
                    </p>
                    <p className='text-[10px] text-[#6F7376]'>
                      {calendarDateFormat(item.created_at)}
                    </p>
                  </div>
                </div>
                <div
                  className={`w-[10px] h-[10px] rounded-full ${
                    item.tx_type === 'reversal' ? 'bg-[#E60A2B]' : 'bg-[#EDEDEE]'
                  }`}></div>
              </div>
            ))}
          </InfiniteScroll>
        )}
      </div>
    </SideModal>
  );
};

export default WalletTransactionsModal;
